import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/apiClient';
import type { WhatsAppStatus } from '../api/whatsapp';
import { useAuth } from '../contexts/AuthContext';

export function useWhatsAppStatus() {
  return useQuery<WhatsAppStatus>({
    queryKey: ['whatsapp', 'status'],
    queryFn: async () => {
      const { data } = await api.get<WhatsAppStatus>('/api/whatsapp/status');
      return data;
    },
    // No polling! Status updates via Supabase Realtime (useWhatsAppRealtime hook)
    staleTime: 5 * 60 * 1000, // Consider data fresh for 5 minutes
  });
}

export function useWhatsAppIncompleteSync() {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  return useQuery({
    queryKey: ['whatsapp', 'incomplete-sync'],
    queryFn: async () => {
      const syncActive = localStorage.getItem('whatsapp_sync_active') === 'true';
      const syncStartedAt = localStorage.getItem('whatsapp_sync_started_at');

      if (!syncActive || !syncStartedAt) {
        return { hasIncompleteSync: false };
      }

      const elapsed = Date.now() - parseInt(syncStartedAt, 10);

      // If >60s, assume completed/failed
      if (elapsed > 60000) {
        localStorage.setItem('whatsapp_sync_active', 'false');
        return { hasIncompleteSync: false };
      }

      // Check if messages exist (sync completed)
      const chatsData = queryClient.getQueryData(['whatsapp', 'chats']) as any;
      const hasMessages = chatsData?.chats?.some((chat: any) =>
        chat.last_message_text || chat.unread_count > 0
      );

      if (hasMessages) {
        localStorage.setItem('whatsapp_sync_active', 'false');
        return { hasIncompleteSync: false };
      }

      return {
        hasIncompleteSync: true,
        startedAt: parseInt(syncStartedAt, 10),
        elapsedTime: elapsed,
      };
    },
    enabled: !!user,
    staleTime: Infinity,
  });
}

export function useConnectWhatsApp() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      const { data } = await api.post('/api/whatsapp/connect');
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['whatsapp', 'status'] });
    },
  });
}

export function useDisconnectWhatsApp() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      const { data } = await api.post('/api/whatsapp/disconnect');
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['whatsapp'] });
    },
  });
}
